'use client';
import {
  CHECKOUT_START_DATETIME,
  PACKAGE_STORAGE,
  SOCIAL_LOGIN_REDIRECT,
} from '@config/storageKeys';
import { useAppBottomHeaderRef } from '@hooks/use-app-bottom-header';
import { usePackagesParams } from '@hooks/use-packages-params';
import useStorage from '@hooks/useStorage';
import { type IFormValues } from '@interfaces/buildPackage';
import { useCheckoutStepsStore } from '@store/checkout-steps-store';
import { trackGa4PackageComponentRemoved } from '@v2/analytics/ga4';
import { useIsClient } from '@uidotdev/usehooks';
import { links } from '@utils/navigation/links';
import { skipPackageComponent } from '@utils/skip-package-component';
import { Button } from '@v2/ui';
import { useGetHotels } from '../../hooks/use-hotels-list';
import { getTotalPriceLabel } from '@v2/views/SearchTickets/components/utils/get-total-price-label';
import { setCookie } from 'cookies-next';
import Link from 'next/link';
import { memo, useState } from 'react';
import { useHotelEstimate } from '../../hooks/use-hotel-estimate';

export const MobileBottomHeader = memo(function MobileBottomHeader() {
  const bottomHeaderRef = useAppBottomHeaderRef<HTMLDivElement>();
  const storage = useStorage();
  const [currentPackageData] = useState<IFormValues>(
    JSON.parse(storage.getItem(PACKAGE_STORAGE) ?? '{}')
  );
  const { packageType, eventId, eventLink } = usePackagesParams();

  const { lowestHotelPrice, canSearchHotels, hasCurrentData } = useGetHotels();
  const { quote } = useHotelEstimate({
    kind: 'list',
    hotelPrice: lowestHotelPrice,
    ready: canSearchHotels && hasCurrentData,
  });
  const isClient = useIsClient();

  const bottomTotalPriceLabel = getTotalPriceLabel({
    isFlightIncluded: currentPackageData?.selected?.flights,
    isHotelIncluded: true,
    isTicketIncluded:
      packageType === 'packages' || currentPackageData?.selected?.tickets,
  });

  if (!isClient || !quote.display) return null;

  return (
    <div
      ref={bottomHeaderRef}
      data-app-bottom-header="true"
      className="app-bottom-header fixed bottom-0 left-0 right-0 z-20 w-full bg-gray-900 pb-[env(safe-area-inset-bottom)]"
    >
      <div className="mx-auto flex max-w-[1600px] flex-col items-stretch justify-between gap-3 px-4 py-4 lg:flex-row lg:items-center lg:px-12">
        <div className="flex items-start justify-between gap-x-3 lg:justify-start">
          <div>
            <h5 className="text-base font-bold text-white lg:text-lg">
              Estimated {quote.display.perPerson} per person
            </h5>

            <span className="block text-xs font-medium text-gray-400 lg:text-sm">
              {quote.display.total} in total. {bottomTotalPriceLabel}{' '}
            </span>
          </div>
        </div>

        <Button
          asChild
          variant="ghost"
          className="min-h-11 shrink-0 border border-gray-700 bg-transparent px-4 text-sm text-white"
        >
          <Link
            href={`${links.checkout}?eventId=${eventId}&eventLink=${eventLink}&packageType=${packageType}`}
            onClick={() => {
              useCheckoutStepsStore.setState({ hotelSelected: false });
              if (skipPackageComponent(storage, 'hotel')) {
                trackGa4PackageComponentRemoved({
                  component: 'hotel',
                  params: {
                    event_id: eventId,
                    package_type:
                      packageType === 'packages' ? 'package' : 'tickets_only',
                  },
                });
              }
              setCookie(CHECKOUT_START_DATETIME, new Date().getTime());
              storage.setItem(
                SOCIAL_LOGIN_REDIRECT,
                `${links.checkout}?eventId=${eventId}&eventLink=${eventLink}&packageType=${packageType}`
              );
            }}
          >
            {"I don't need a hotel"}
          </Link>
        </Button>
      </div>
    </div>
  );
});
